import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class GooglePlacesService {
  private googleMapsScriptLoadingPromise: Promise<void>;

  constructor() {}

  /**
   * Loads the Google Maps API asynchronously and returns an Observable
   * that emits a void value when the API has finished loading.
   *
   * @returns {Observable<void>} An Observable that emits a void value
   * when the Google Maps API has finished loading.
   */
  loadGoogleMaps(): Observable<void> {
    if (!this.googleMapsScriptLoadingPromise) {
      this.googleMapsScriptLoadingPromise = new Promise<void>((resolve) => {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.defer = true;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsApi_string}&libraries=places&callback=Function.prototype`;
        script.onload = () => resolve();
        document.head.appendChild(script);
      });
    }

    return from(this.googleMapsScriptLoadingPromise);
  }

  /**
   * Transforms an array of Google Maps GeocoderAddressComponent objects
   * into a structured address object.
   *
   * @param {google.maps.GeocoderAddressComponent[]} addressComponents
   * - An array of Google Maps GeocoderAddressComponent objects.
   * @returns {object} - The transformed address object.
   * - address: The combined street number and route of the address.
   * - suburb: The locality of the address.
   * - state: The administrative area level 1 of the address.
   * - postcode: The postal code of the address.
   */
  transformAddress(
    addressComponents: google.maps.GeocoderAddressComponent[]
  ) {
    const address: Record<string, string> = {
      street_number: '',
      route: '',
      locality: '',
      administrative_area_level_1: '',
      postal_code: '',
    };

    addressComponents?.forEach((component) => {
      const componentType = component.types[0];
      const componentName = componentType === 'administrative_area_level_1'
        ? component.short_name
        : component.long_name;
      address[componentType] = componentName;
    });

    return {
      streetAddress: [address['street_number'], address['route']]
        .join(' ')
        .trim(),
      suburb: address['locality'],
      state: address['administrative_area_level_1'],
      postcode: address['postal_code'],
    };
  }
}
